import {
  signInAnonymously, onAuthStateChanged, GoogleAuthProvider, linkWithPopup, signInWithPopup, signInWithCredential,
  EmailAuthProvider, linkWithCredential, signOut as fbSignOut,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
  linkWithRedirect, signInWithRedirect, getRedirectResult, type User,
} from 'firebase/auth'
import { doc, setDoc, getDocs, collection, serverTimestamp, increment } from 'firebase/firestore'
import { auth, db, usingEmulator } from './firebase'
import { load, save } from './storage'
import type { DayResult } from './types'

let currentUser: User | null = null
const listeners = new Set<() => void>()
export function onUserChange(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn) } }
export function getUser() { return currentUser }

let plus = false
export function isPlus() { return plus || (usingEmulator && localStorage.getItem('wayoff.devPremium') === '1') }

/** True while a magic-link completion is in flight (so the anon fallback doesn't race it). */
let completingLink = false

/** Boot: complete a magic link if we arrived from one, else silent anonymous sign-in; keep profile + results synced. */
export function initAuth() {
  completingLink = isSignInWithEmailLink(auth, location.href)
  if (completingLink) void completeMagicLink()
  // complete a redirect-based Google sign-in (the popup-blocked fallback path)
  getRedirectResult(auth).catch((e) => console.warn('redirect sign-in failed', e))
  onAuthStateChanged(auth, async (u) => {
    currentUser = u
    if (!u) {
      plus = false; listeners.forEach((f) => f())
      if (!completingLink) signInAnonymously(auth).catch((e) => console.warn('anon auth failed', e))
      return
    }
    try {
      const tok = await u.getIdTokenResult(true) // force-refresh so admin/Stripe grants appear on next app load
      plus = tok.claims.stripeRole === 'plus'
    } catch { plus = false }
    listeners.forEach((f) => f())
    try {
      await setDoc(doc(db, 'users', u.uid), {
        lastSeen: serverTimestamp(),
        provider: u.providerData[0]?.providerId ?? 'anon',
        ...(u.metadata.creationTime === u.metadata.lastSignInTime ? { createdAt: serverTimestamp() } : {}),
      }, { merge: true })
      await pushLocalResults(u.uid)
      await pullCloudResults(u.uid)
    } catch (e) { console.warn('user sync failed', e) }
  })
}

/**
 * Durable copy of one played game (daily core, extended, bonus, backfill).
 * Doc id may be a date, `date-ext`, or `2026-Wnn-bonus`. Also bumps the O(1) lifetime
 * aggregates on the user doc — bounded size, powers stats without subcollection reads.
 */
export async function syncResult(r: DayResult, docId = r.date) {
  const u = currentUser
  if (!u) return
  try {
    await setDoc(doc(db, 'users', u.uid, 'results', docId), { ...r, syncedAt: serverTimestamp() }, { merge: true })
    const sharp = r.results.filter((x) => x.points >= 4).length
    await setDoc(doc(db, 'users', u.uid), {
      lifetime: {
        games: increment(1), totalScore: increment(r.score), sharp: increment(sharp),
        byTheme: { [r.theme.id]: { games: increment(1), total: increment(r.score) } },
      },
    }, { merge: true })
  } catch (e) { console.warn('result sync failed', e) }
}

async function pushLocalResults(uid: string) {
  const s = load()
  for (const r of Object.values(s.results)) {
    if ((r as DayResult & { synced?: boolean }).synced) continue
    await setDoc(doc(db, 'users', uid, 'results', r.date), { ...r, syncedAt: serverTimestamp() }, { merge: true })
    ;(r as DayResult & { synced?: boolean }).synced = true
  }
  save(s)
}

/** New device: bring cloud results down. Local copy wins ties (it has freshest percentile). */
async function pullCloudResults(uid: string) {
  const snap = await getDocs(collection(db, 'users', uid, 'results'))
  const s = load()
  let changed = false
  for (const d of snap.docs) {
    if (!s.results[d.id]) { s.results[d.id] = d.data() as DayResult; changed = true }
  }
  if (changed) save(s)
}

export async function saveProgressWithGoogle() {
  const u = currentUser
  const p = new GoogleAuthProvider()
  try {
    if (u?.isAnonymous) await linkWithPopup(u, p)
    else await signInWithPopup(auth, p)
  } catch (e: any) {
    // account already exists: sign in to it instead (local results merge up on auth change).
    // The failed link attempt already carries the Google credential — reuse it so the
    // player never sees a second popup.
    if (e?.code === 'auth/credential-already-in-use' || e?.code === 'auth/email-already-in-use') {
      const cred = GoogleAuthProvider.credentialFromError(e)
      if (cred) { await signInWithCredential(auth, cred); return }
      await signInWithPopup(auth, p)
      return
    }
    // popup blocked (common on mobile): full-page redirect works everywhere
    if (e?.code === 'auth/popup-blocked' || e?.code === 'auth/cancelled-popup-request') {
      if (u?.isAnonymous) await linkWithRedirect(u, p)
      else await signInWithRedirect(auth, p)
      return
    }
    throw e
  }
}

const ML_KEY = 'wayoff.magicLinkEmail'

/** Passwordless email: send the sign-in link. Completion happens on the return visit. */
export async function sendMagicLink(email: string): Promise<void> {
  await sendSignInLinkToEmail(auth, email, { url: location.origin + '/', handleCodeInApp: true })
  localStorage.setItem(ML_KEY, email)
}

async function completeMagicLink() {
  try {
    await auth.authStateReady() // wait for the restored (anonymous) session before deciding link-vs-signin
    let email = localStorage.getItem(ML_KEY)
    if (!email) email = prompt('Confirm your email to finish signing in') ?? ''
    if (!email) return
    const u = auth.currentUser
    if (u?.isAnonymous) {
      // link keeps the same uid — streak/results/stats survive
      try { await linkWithCredential(u, EmailAuthProvider.credentialWithLink(email, location.href)) }
      catch (e: any) {
        if (e?.code === 'auth/email-already-in-use' || e?.code === 'auth/credential-already-in-use') {
          await signInWithEmailLink(auth, email, location.href) // existing account: sign into it, local results merge up
        } else throw e
      }
    } else {
      await signInWithEmailLink(auth, email, location.href)
    }
    localStorage.removeItem(ML_KEY)
  } catch (e) {
    console.warn('magic link completion failed', e)
  } finally {
    completingLink = false
    if (!auth.currentUser) signInAnonymously(auth).catch(() => {})
    history.replaceState(null, '', location.origin + '/' + location.hash) // strip oobCode etc.
  }
}

/** Deterministic placeholder for guests, e.g. "Space Guest 4821". Guests cannot set a custom name. */
export function guestName(uid?: string): string {
  const id = uid ?? currentUser?.uid ?? ''
  let h = 0
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 10000
  return `Space Guest ${String(h).padStart(4, '0')}`
}

export function isSignedIn(): boolean { return !!currentUser && !currentUser.isAnonymous }

export async function signOut() { await fbSignOut(auth) } // auth listener re-anons
export async function refreshEntitlement() {
  const u = currentUser; if (!u) return
  const tok = await u.getIdTokenResult(true)
  plus = tok.claims.stripeRole === 'plus'
  listeners.forEach((f) => f())
}

export const NAME_RE = /^[A-Za-z0-9_-]{3,20}$/

/** Claim/rename the unique username via the claimUsername function (race-safe). */
export async function setUsername(name: string): Promise<void> {
  const u = currentUser
  if (!u || u.isAnonymous) throw new Error('Sign in to set a username')
  const trimmed = name.trim().replace(/^@+/, '')
  if (!NAME_RE.test(trimmed)) throw new Error('3–20 characters: letters, numbers, - and _ only')
  const { getFunctions, httpsCallable } = await import('firebase/functions')
  const { getApps } = await import('firebase/app')
  try {
    await httpsCallable(getFunctions(getApps()[0], 'europe-west2'), 'claimUsername')({ name: trimmed })
  } catch (e: any) {
    const msg = e?.message ?? ''
    // callable errors carry our own human text; anything else gets the generic treatment
    throw new Error(/characters|taken|reserved|Sign in/i.test(msg) ? msg : 'Could not save that username right now — try again in a moment.')
  }
}

/** 1-read availability check against the reverse index. */
export async function usernameAvailable(name: string): Promise<boolean> {
  const key = name.trim().replace(/^@+/, '').toLowerCase()
  if (!NAME_RE.test(name.trim().replace(/^@+/, ''))) return false
  const { getDoc } = await import('firebase/firestore')
  const snap = await getDoc(doc(db, 'usernames', key))
  return !snap.exists() || snap.get('uid') === currentUser?.uid
}

/** Read the player's stored username (falls back to the auth provider's name). */
export async function getUsername(): Promise<string> {
  const u = currentUser
  if (!u) return ''
  const { getDoc } = await import('firebase/firestore')
  const snap = await getDoc(doc(db, 'users', u.uid))
  return (snap.get('displayName') as string) ?? u.displayName ?? ''
}
